/**
 * LivenessUploader — thin wrapper around the HttpClient's uploadWithSession()
 * that adds:
 *   - Progress callbacks (onProgress)
 *   - Upload cancellation via AbortController
 *   - Automatic retry on transient network failure (configurable)
 *
 * Used internally by LivenessModule.create() and LivenessModule.submit() when
 * the caller needs progress reporting (e.g. for a progress bar during video
 * upload on slow connections).
 */

import { HttpClient } from "../../client/HttpClient";
import { MAX_RETRIES, RETRY_DELAY_MS } from "../../utils/constants";
import { SDKLogger } from "../../utils/logger";

export interface UploadOptions {
  /** Called with a value 0–1 as the upload progresses. */
  onProgress?: (fraction: number) => void;

  /** Optional AbortSignal to cancel the upload mid-flight. */
  signal?: AbortSignal;

  /** Override the default number of retries on transient errors. */
  retries?: number;
}

export class LivenessUploader {
  constructor(private http: HttpClient) {}

  /**
   * Upload a FormData payload to `path` with optional progress and
   * cancellation support.
   *
   * Falls back to the standard `uploadWithSession()` on browsers that do not
   * support XMLHttpRequest upload progress (very rare).
   */
  async upload<T>(
    path: string,
    formData: FormData,
    options: UploadOptions = {}
  ): Promise<T> {
    const { onProgress, signal, retries = MAX_RETRIES } = options;

    // If no progress callback is needed, delegate straight to HttpClient
    // which already has retry logic built in.
    if (!onProgress) {
      return this.http.uploadWithSession<T>(path, formData);
    }

    return this.uploadWithProgress<T>(path, formData, onProgress, signal, retries);
  }

  // ── Private ────────────────────────────────────────────────────────────────

  private async uploadWithProgress<T>(
    path: string,
    formData: FormData,
    onProgress: (fraction: number) => void,
    signal: AbortSignal | undefined,
    retriesLeft: number
  ): Promise<T> {
    const sessionToken = (this.http as any).sessionToken as string | null;
    if (!sessionToken) {
      throw new Error("SDK: No session token. Call sdk.createSession() first.");
    }

    const baseUrl: string = (this.http as any).baseUrl as string;

    return new Promise<T>((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Propagate AbortSignal to XHR
      if (signal) {
        if (signal.aborted) {
          reject(new DOMException("Upload cancelled", "AbortError"));
          return;
        }
        signal.addEventListener("abort", () => xhr.abort(), { once: true });
      }

      xhr.open("POST", `${baseUrl}${path}`, true);
      xhr.setRequestHeader("x-access-token", sessionToken);

      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable) {
          onProgress(evt.loaded / evt.total);
        }
      };

      xhr.onload = () => {
        onProgress(1);

        let envelope: any;
        try {
          envelope = JSON.parse(xhr.responseText);
        } catch {
          reject(new Error(`SDK: Non-JSON response from ${path} (${xhr.status})`));
          return;
        }

        if (xhr.status >= 200 && xhr.status < 300 && envelope.status !== "error") {
          SDKLogger.info(`Upload to ${path} succeeded`, { status: xhr.status });
          resolve(envelope.data as T);
        } else {
          const err = new Error(envelope.message || `Upload to ${path} failed (${xhr.status})`);
          (err as any).statusCode = xhr.status;
          (err as any).code = envelope.code;
          reject(err);
        }
      };

      xhr.onerror = async () => {
        SDKLogger.warn(`Upload to ${path} failed — network error`, { retriesLeft });

        if (retriesLeft > 0) {
          await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
          try {
            const result = await this.uploadWithProgress<T>(
              path, formData, onProgress, signal, retriesLeft - 1
            );
            resolve(result);
          } catch (retryErr) {
            reject(retryErr);
          }
        } else {
          reject(new Error(`SDK: Upload to ${path} failed after all retries.`));
        }
      };

      xhr.onabort = () => {
        reject(new DOMException("Upload cancelled by user", "AbortError"));
      };

      xhr.send(formData);
    });
  }
}
